import {
  Appear,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Flex,
  Icon,
  Link,
  Placement,
  PlacementManager,
  PlacementReference,
  ProgressSpinner,
  Text,
} from '@bitrise/bitkit';
import { TypeCrashStatus } from '@trace/types';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';

import { CRASH_STATUS_ICON_MAP } from '../../../constants/Crashes';
import dataAtIds from '../../../dataAtIds.json';
import useCrashList from '../useCrashList';
import { trackEvent } from '../../../utils/tracking';
import Attributes from '../../Attributes/Attributes';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import TimeRange from '../../TimeRange/TimeRange';
import { URLStateContext } from '../../URLState/URLState';
import CrashesTable from './CrashesTable';

const statuses: Record<TypeCrashStatus, string> = {
  open: 'Open',
  closed: 'Closed',
};

const CrashListPage: FunctionComponent = () => {
  const [isFilterStatusVisible, setFilterStatusVisible] = useState(false);
  const { crashStatus, updateUrlState, crashSortKey, crashSortDirection } = useContext(
    URLStateContext,
  );
  const { crashes, refetch, isError, isFetched, isFetching } = useCrashList({
    status: crashStatus,
    sortKey: crashSortKey,
    sortDirection: crashSortDirection,
  });

  /**
   * Use a side-effect to:
   *  - Track visits to this page (i.e. whenever this component is mounted)
   */
  useEffect(() => {
    trackEvent('IssueList.Visit', {});
  }, []);

  const handleToggleFilterStatusMenu = () => {
    setFilterStatusVisible(!isFilterStatusVisible);
  };

  const handleChangeFilterStatus = (status?: TypeCrashStatus) => {
    updateUrlState({ crashStatus: status || 'all' });
    setFilterStatusVisible(false);
    trackEvent('IssuesList.Filter', { status: status || 'all' });
  };

  return (
    <Attributes source="rollup">
      <Page data-trace-at-id={dataAtIds.CrashListPage}>
        <PageHeader>
          <PageTitle>Crashes</PageTitle>

          <Flex alignChildrenHorizontal="end" direction="horizontal" gap="x4" margin="x5">
            <Flex>
              <PlacementManager>
                <PlacementReference>
                  {({ ref }) => (
                    <Button
                      alignChildrenHorizontal="start"
                      data-trace-at-id={dataAtIds.CrashListPageStatusFilterMenuToggle}
                      innerRef={ref}
                      level="tertiary"
                      onClick={handleToggleFilterStatusMenu}
                    >
                      <Icon
                        name={
                          crashStatus !== 'all'
                            ? CRASH_STATUS_ICON_MAP[crashStatus]
                            : 'TestInconclusive'
                        }
                      />

                      <Text titlecase>
                        {crashStatus !== 'all'
                          ? `Status: ${statuses[crashStatus]}`
                          : 'All Statuses'}
                      </Text>
                      <Icon name="DropdownArrows" />
                    </Button>
                  )}
                </PlacementReference>

                <Placement onClose={handleToggleFilterStatusMenu} visible={isFilterStatusVisible}>
                  {({ width }) => (
                    <DropdownMenu minWidth={width}>
                      <DropdownMenuItem
                        onClick={() => handleChangeFilterStatus()}
                        selected={crashStatus === 'all'}
                        data-trace-at-id={dataAtIds.CrashListPageStatusFilterMenuItem}
                      >
                        All Statuses
                      </DropdownMenuItem>

                      {Object.entries(statuses).map(([value, title]) => (
                        <DropdownMenuItem
                          key={value}
                          onClick={() => handleChangeFilterStatus(value as TypeCrashStatus)}
                          selected={crashStatus && crashStatus === value}
                          data-trace-at-id={dataAtIds.CrashListPageStatusFilterMenuItem}
                        >
                          <Text titlecase>{title}</Text>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenu>
                  )}
                </Placement>
              </PlacementManager>
            </Flex>

            <Flex>
              <TimeRange />
            </Flex>
          </Flex>
        </PageHeader>

        <PageBody data-trace-at-id={dataAtIds.CrashListPage}>
          {isFetching && crashes.length === 0 ? (
            <Flex
              alignChildren="middle"
              data-trace-at-id={dataAtIds.CrashListPageLoadingIndicator}
              direction="vertical"
              grow
            >
              <Appear animation="Fade" textColor="grape-3">
                <ProgressSpinner />
              </Appear>
            </Flex>
          ) : (
            <>
              {isError && (
                <Flex
                  alignChildrenVertical="middle"
                  direction="vertical"
                  grow
                  data-trace-at-id={dataAtIds.CrashListPageErrorMessage}
                >
                  <Appear animation="Fade" textColor="red-3">
                    <Flex alignChildren="middle" direction="vertical" height="100%">
                      <Flex>
                        <Flex alignChildren="middle" direction="vertical">
                          <Icon margin="x1" name="BitbotFailed" size="3rem" />
                        </Flex>
                        <Text align="middle" ellipsis>
                          Uh oh, there&apos;s been a problem.
                        </Text>
                        <Text align="middle">
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <Link clickable onClick={refetch} underline>
                            Click here to retry
                          </Link>
                          .
                        </Text>
                      </Flex>
                    </Flex>
                  </Appear>
                </Flex>
              )}

              {isFetched && crashes.length === 0 && (
                <Flex
                  alignChildrenVertical="middle"
                  data-trace-at-id={dataAtIds.CrashListPageNoDataMessage}
                  direction="vertical"
                  grow
                >
                  <Appear animation="Fade">
                    <Flex alignChildren="middle" direction="vertical" height="100%">
                      <Flex>
                        <Text align="middle">There are no crashes.</Text>
                        <Text align="middle">
                          Try expanding your selected time range, or selecting a different issue
                          status.
                        </Text>
                      </Flex>
                    </Flex>
                  </Appear>
                </Flex>
              )}

              {isFetched && crashes.length > 0 && <CrashesTable crashes={crashes} />}
            </>
          )}
        </PageBody>
      </Page>
    </Attributes>
  );
};

export default CrashListPage;
